import { Plus, X } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import type { StepErrors, TriggerDraft } from '../types';

const KIND_OPTIONS: { value: TriggerDraft['kind']; label: string }[] = [
  { value: 'cron', label: 'Cron schedule' },
  { value: 'webhook', label: 'Incoming webhook' },
  { value: 'event', label: 'Platform event' },
];

function summarize(item: TriggerDraft): string {
  if (item.kind === 'cron') {
    return item.cron ?? '';
  }
  if (item.kind === 'event') {
    return [item.channel, (item.eventTypes ?? []).join(', ')].filter(Boolean).join(' → ');
  }
  return 'Incoming webhook';
}

export interface TriggerBuilderProps {
  items: TriggerDraft[];
  onChange: (next: TriggerDraft[]) => void;
  /** Unused in P2 gating (add is validated locally), kept for renderer parity. */
  errors?: StepErrors;
}

/**
 * attach-webhook form (design §14.1): one "add" form per chosen kind, rows
 * removable. Items are INTENTS — each is POSTed post-create via
 * `POST /agent/factories/:id/triggers`. The form fields map onto the real
 * `CreateFactoryTriggerDto` (`{kind, cron?, eventFilter?}`) — the contract has
 * no description/name members, so none are collected (Q2 resolved at T6;
 * never invent inputs the backend cannot persist).
 */
export function TriggerBuilder({ items, onChange }: TriggerBuilderProps) {
  const [kind, setKind] = useState<TriggerDraft['kind']>('cron');
  const [schedule, setSchedule] = useState('');
  const [channel, setChannel] = useState('');
  const [eventTypes, setEventTypes] = useState('');
  const [formErrors, setFormErrors] = useState<StepErrors>({});

  const parsedEventTypes = eventTypes
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

  const addTrigger = () => {
    const nextErrors: StepErrors = {};
    if (kind === 'cron' && schedule.trim() === '') {
      nextErrors.schedule = 'Enter a schedule, e.g. 0 9 * * 1-5.';
    }
    if (kind === 'event') {
      if (channel.trim() === '') {
        nextErrors.channel = 'Enter a channel, e.g. repositories.';
      }
      if (parsedEventTypes.length === 0) {
        nextErrors.eventTypes = 'Enter at least one event type.';
      }
    }
    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    const item: TriggerDraft =
      kind === 'cron'
        ? { kind: 'cron', cron: schedule.trim() }
        : kind === 'event'
          ? { kind: 'event', channel: channel.trim(), eventTypes: parsedEventTypes }
          : { kind: 'webhook' };
    onChange([...items, item]);
    setSchedule('');
    setChannel('');
    setEventTypes('');
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">Trigger type</legend>
        <div className="flex flex-wrap gap-4">
          {KIND_OPTIONS.map((option) => (
            <Label key={option.value} className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="radio"
                name="trigger-kind"
                value={option.value}
                checked={kind === option.value}
                onChange={() => {
                  setKind(option.value);
                  setFormErrors({});
                }}
                className="h-4 w-4 accent-primary"
              />
              {option.label}
            </Label>
          ))}
        </div>
      </fieldset>

      {kind === 'cron' && (
        <div className="space-y-1.5">
          <Label htmlFor="trigger-schedule">Schedule</Label>
          <Input
            id="trigger-schedule"
            value={schedule}
            onChange={(event) => {
              setSchedule(event.target.value);
              if (event.target.value.trim() !== '') {
                setFormErrors(({ schedule: _ignored, ...rest }) => rest);
              }
            }}
            placeholder="e.g. 0 9 * * 1-5"
            className="font-mono text-sm"
            aria-invalid={formErrors.schedule ? true : undefined}
            aria-describedby={formErrors.schedule ? 'trigger-schedule-error' : undefined}
            onKeyDown={(event) => {
              // Enter adds the trigger instead of submitting the step form.
              if (event.key === 'Enter') {
                event.preventDefault();
                addTrigger();
              }
            }}
          />
          <p className="text-sm text-muted-foreground">Cron expression in UTC.</p>
          {formErrors.schedule ? (
            <p id="trigger-schedule-error" className="text-sm text-destructive">
              {formErrors.schedule}
            </p>
          ) : null}
        </div>
      )}

      {kind === 'webhook' && (
        <p className="text-sm text-muted-foreground">
          The webhook URL is revealed once, right after the agent is created.
        </p>
      )}

      {kind === 'event' && (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="trigger-channel">Channel</Label>
            <Input
              id="trigger-channel"
              value={channel}
              onChange={(event) => {
                setChannel(event.target.value);
                if (event.target.value.trim() !== '') {
                  setFormErrors(({ channel: _ignored, ...rest }) => rest);
                }
              }}
              placeholder="e.g. repositories"
              className="font-mono text-sm"
              aria-invalid={formErrors.channel ? true : undefined}
              aria-describedby={formErrors.channel ? 'trigger-channel-error' : undefined}
            />
            <p className="text-sm text-muted-foreground">The events channel to listen to.</p>
            {formErrors.channel ? (
              <p id="trigger-channel-error" className="text-sm text-destructive">
                {formErrors.channel}
              </p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="trigger-event-types">Event types</Label>
            <Input
              id="trigger-event-types"
              value={eventTypes}
              onChange={(event) => {
                setEventTypes(event.target.value);
                if (parsedEventTypes.length > 0) {
                  setFormErrors(({ eventTypes: _ignored, ...rest }) => rest);
                }
              }}
              placeholder="e.g. repositories:pr-opened"
              className="font-mono text-sm"
              aria-invalid={formErrors.eventTypes ? true : undefined}
              aria-describedby={formErrors.eventTypes ? 'trigger-event-types-error' : undefined}
              onKeyDown={(event) => {
                // Enter adds the trigger instead of submitting the step form.
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addTrigger();
                }
              }}
            />
            <p className="text-sm text-muted-foreground">Comma-separated.</p>
            {formErrors.eventTypes ? (
              <p id="trigger-event-types-error" className="text-sm text-destructive">
                {formErrors.eventTypes}
              </p>
            ) : null}
          </div>
        </div>
      )}

      <Button type="button" variant="secondary" size="sm" onClick={addTrigger}>
        <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
        Add trigger
      </Button>

      <div className="space-y-2">
        <p className="text-sm font-medium">Your triggers</p>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No triggers yet.</p>
        ) : (
          <ul className="space-y-2">
            {items.map((item, index) => (
              <li
                key={index}
                className="flex h-10 items-center gap-2 rounded-md border bg-card px-3"
              >
                <Badge variant="secondary" className="rounded-sm">
                  {item.kind === 'cron' ? 'Cron' : item.kind === 'webhook' ? 'Webhook' : 'Event'}
                </Badge>
                <span className="min-w-0 flex-1 truncate font-mono text-xs">{summarize(item)}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  aria-label="Remove trigger"
                  onClick={() => removeItem(index)}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
